/**
 * Dove vanno i ricavi del mese: una ciambella e le sue voci.
 *
 * È la risposta alla domanda «di 2.434 € quanto se ne va, e dove?» in un
 * colpo d'occhio: quello che resta, quello che prendono i canali, le pulizie,
 * le imposte, il resto. Sotto, i canali uno per uno con la loro percentuale —
 * il numero con cui riconciliare la fattura di Booking quando arriva.
 */

import { PieChart } from "lucide-react";
import { formatEur, type ProfittoImmobile } from "@otto/shared";
import { Card } from "../ui/primitives.tsx";
import { IntestazioneRiquadro } from "../ui/Sezione.tsx";
import { Ciambella, VoceLegenda } from "../ui/Grafici.tsx";

export function DoveVanno({ profitto: p }: { profitto: ProfittoImmobile }) {
  const r = p.rendiconto;
  const incassato = p.ricaviLordiCents;
  const per = (categoria: string) =>
    p.righe
      .filter((riga) => riga.categoria === categoria)
      .reduce((s, riga) => s + riga.importoCents, 0);
  const canali = per("commissioni_ota");
  const soggiorno = per("tassa_soggiorno");
  const pulizie = per("pulizie");
  const imposte = per("imposte");
  const altro = Math.max(
    0,
    p.costiOperativiCents - canali - soggiorno - pulizie - imposte,
  );
  const resta = Math.max(0, p.utileGestoreCents);
  const trasferimento = p.trasferimentoCents;

  const fette = [
    { nome: "Ti resta", valore: resta, classe: "stroke-ok", pallino: "bg-ok" },
    {
      nome: "Canali",
      valore: canali,
      classe: "stroke-accent",
      pallino: "bg-accent",
    },
    {
      nome: "Imposta di soggiorno",
      valore: soggiorno,
      classe: "stroke-nav-ink",
      pallino: "bg-nav-ink",
    },
    {
      nome: "Pulizie",
      valore: pulizie,
      classe: "stroke-warn",
      pallino: "bg-warn",
    },
    {
      nome: "Imposte",
      valore: imposte,
      classe: "stroke-ink-soft",
      pallino: "bg-ink-soft",
    },
    {
      nome: "Altri costi",
      valore: altro,
      classe: "stroke-line-strong",
      pallino: "bg-line-strong",
    },
    ...(trasferimento > 0 && p.tipoRapporto === "affitto"
      ? [
          {
            nome: "Al proprietario",
            valore: trasferimento,
            classe: "stroke-danger",
            pallino: "bg-danger",
          },
        ]
      : []),
  ];
  const trattenute = r.canali.filter(
    (c) => c.commissioneCents + c.incassoCents > 0,
  );

  return (
    <Card className="flex h-full flex-col p-5">
      <IntestazioneRiquadro
        icon={<PieChart size={15} />}
        titolo="Dove vanno i ricavi"
      />

      <Ciambella
        fette={fette}
        centro={{ valore: formatEur(incassato), etichetta: "incassati" }}
      />

      <div className="mt-4 divide-y divide-line">
        {fette
          .filter((f) => f.valore > 0)
          .map((f) => (
            <VoceLegenda
              key={f.nome}
              classe={f.pallino}
              nome={f.nome}
              valore={formatEur(f.valore)}
            />
          ))}
      </div>

      {trattenute.length > 0 ? (
        <div className="mt-4 border-t border-line pt-3">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
            Per canale, senza IVA
          </p>
          {trattenute.map((c) => (
            <div
              key={c.canale}
              className="flex items-baseline justify-between gap-3 py-1.5 text-[13.5px]"
            >
              <span className="min-w-0 capitalize text-ink sm:truncate">
                {c.canale}
                <span className="normal-case text-ink-faint">
                  {" "}
                  · {c.prenotazioni}{" "}
                  {c.prenotazioni === 1 ? "prenotazione" : "prenotazioni"}
                  {c.trattenutaNelPayout ? " · già trattenuti" : " · da pagare"}
                  {c.convenzioneVerificata
                    ? ""
                    : " · convenzione non verificata"}
                </span>
              </span>
              <span className="shrink-0 font-bold tabular-nums text-ink">
                {/* La cifra somma commissione e costo di incasso: basta che
                    uno dei due sia stimato perché il totale lo sia. */}
                {c.stimato || c.incassoStimato ? (
                  <span className="mr-0.5 text-ink-faint">≈</span>
                ) : null}
                {formatEur(c.commissioneCents + c.incassoCents)}
              </span>
            </div>
          ))}
          <p className="mt-2 text-[11.5px] leading-relaxed text-ink-faint">
            Da confrontare con la fattura del canale. L&apos;IVA è a parte.
          </p>
        </div>
      ) : null}
    </Card>
  );
}
